import { stringify } from "qs";

// import createKey from "~/lib/cache/createKey";
import { PAGINATION_LIMIT, PAYLOAD_URL } from "~/constants/all";
import { logError, logInfo } from "~/lib/logger";
import { handleError } from "~/lib/payload/errors";
import rest, { fileRest, restPublic } from "~/lib/payload/rest";
import type {
  ApiResponse,
  BulkDeleteCollectionItems,
  CollectionData,
  CollectionDataWithId,
  CollectionDataWithIds,
  CollectionFile,
  CollectionFileWithId,
  CollectionJoinParams,
  CollectionNames,
  DeleteCollectionItem,
  ErrorResponse,
  GetAllCollectionItems,
  GetCollectionItem,
  PagedCollectionResponse,
  PaginationWithCollection,
  PayloadBulkResponse,
  PayloadCollectionResponse,
  PayloadGetResponse,
  PayloadSingleResponse,
  WhereClause,
} from "~/types";

const createJoinString = (
  joins: Array<{
    collection: CollectionNames;
    limit?: number;
    where?: object;
    depth?: number;
  }>
) => {
  return joins
    .map((join) => {
      const { collection, ...parameters } = join;
      const baseParam = `joins[${collection}]`;

      // If no additional parameters, return just the collection marker
      if (Object.keys(parameters).length === 0) return baseParam;

      // Build parameters for each option
      return Object.entries(parameters)
        .map(([key, value]) => {
          // Handle the where object by JSON stringifying it
          if (key === "where" && typeof value === "object") {
            // This will handle nested objects like { roles: { in: "owner" } }
            return `${baseParam}[${key}]=${encodeURIComponent(JSON.stringify(value))}`;
          }
          return `${baseParam}[${key}]=${value ?? ""}`;
        })
        .join("&");
    })
    .join("&");
};

/**
 * Creates a new item in the specified collection
 * @param collection - The name of the collection
 * @param data - The data for the new item
 * @returns A promise that resolves to the API response
 */
export const createCollectionItem = async <T = any>({
  collection,
  data,
}: CollectionData): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Creating collection item", {
      collection,
      data,
      message: "Collection is required",
      function: "createCollectionItem",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Creating collection item", {
      collection,
      data,
      message: "Data is required",
      function: "createCollectionItem",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Creating collection item", {
      collection,
      data,
      function: "createCollectionItem",
    });

    const res = await rest<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}`,
      data,
      {
        method: "POST",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Creating collection item failed",
        "createCollectionItem"
      );
    }

    return {
      success: true,
      data: res?.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Creating collection item failed",
      "createCollectionItem"
    );
  }
};

/**
 * Creates a new item with a file in the specified collection
 * @param collection - The name of the collection
 * @param file - The file to upload
 * @param data - The data for the new item
 * @param signal - The abort signal
 * @returns A promise that resolves to the API response
 */
export const createCollectionFileItem = async <T = any>({
  collection,
  file,
  data,
  signal,
}: CollectionFile): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Creating collection file item", {
      collection,
      data,
      message: "Collection is required",
      function: "createCollectionFileItem",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!file) {
    logError("payloadApiCollections", "Creating collection file item", {
      collection,
      data,
      message: "File is required",
      function: "createCollectionFileItem",
    });

    return {
      success: false,
      message: "File is required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Creating collection file item", {
      collection,
      data,
      message: "Data is required",
      function: "createCollectionFileItem",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  const formData = new FormData();

  formData.append("file", file);
  formData.append("_payload", JSON.stringify(data));

  try {
    logInfo("payloadApiCollections", "Creating collection file item", {
      collection,
      data,
      function: "createCollectionFileItem",
    });

    const res = await fileRest<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}`,
      formData,
      {
        method: "POST",
        signal,
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Creating collection file item failed",
        "createCollectionFileItem"
      );
    }

    return {
      success: true,
      data: res?.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Creating collection file item failed",
      "createCollectionFileItem"
    );
  }
};

/**
 * Updates an file item in the specified collection by ID
 * @param id - The ID of the item to update
 * @param collection - The name of the collection
 * @param file - The file to upload
 * @param data - The data for the new item
 * @param signal - The abort signal
 * @returns A promise that resolves to the API response
 */
export const updateCollectionFileItemById = async <T = any>({
  collection,
  id,
  file,
  data,
  signal,
}: CollectionFileWithId): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Updating collection file item by ID", {
      collection,
      id,
      data,
      message: "Collection is required",
      function: "updateCollectionFileItemById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!id) {
    logError("payloadApiCollections", "Updating collection file item by ID", {
      collection,
      id,
      data,
      message: "ID is required",
      function: "updateCollectionFileItemById",
    });

    return {
      success: false,
      message: "ID is required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Updating collection file item by ID", {
      collection,
      id,
      data,
      message: "Data is required",
      function: "updateCollectionFileItemById",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  const formData = new FormData();

  // The file may not be updating the file, so only append it if it exists.
  if (file) {
    formData.append("file", file);
  }

  formData.append("_payload", JSON.stringify(data));

  try {
    logInfo("payloadApiCollections", "Updating collection file item by ID", {
      collection,
      id,
      data,
      function: "updateCollectionFileItemById",
    });

    const res = await fileRest<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}/${id}`,
      formData,
      {
        method: "PATCH",
        signal,
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Updating collection item by ID failed",
        "updateCollectionFileItemById"
      );
    }

    return {
      success: true,
      data: res?.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Updating collection file item by ID failed",
      "updateCollectionFileItemById"
    );
  }
};

/**
 * Retrieves an item from the specified collection by ID
 * @param options - The options for retrieving the item
 * @returns A promise that resolves to the API response
 */
export const getCollectionItemById = async <T = any>({
  collection,
  id,
  depth,
  join,
}: GetCollectionItem): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      message: "Collection is required",
      function: "getCollectionItemById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!id) {
    logError("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      message: "ID is required",
      function: "getCollectionItemById",
    });

    return {
      success: false,
      message: "ID is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      function: "getCollectionItemById",
    });

    const joinParams = createJoinString(join || []);

    const res = await rest<PayloadGetResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}/${id}?depth=${depth || 1}${joinParams ? `&${joinParams}` : ""}`,
      null,
      {
        method: "GET",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Getting collection item by ID failed",
        "getCollectionItemById"
      );
    }

    return {
      success: true,
      data: res,
    };
  } catch (error) {
    return handleError(
      error,
      "Getting collection item by ID failed",
      "getCollectionItemById"
    );
  }
};

/**
 * Updates an item in the specified collection by ID
 * @param options - The options for updating the item
 * @returns A promise that resolves to the API response
 */
export const updateCollectionItemById = async <T = any>({
  collection,
  id,
  data,
}: CollectionDataWithId): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Updating collection item by ID", {
      collection,
      id,
      data,
      message: "Collection is required",
      function: "updateCollectionItemById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!id) {
    logError("payloadApiCollections", "Updating collection item by ID", {
      collection,
      id,
      data,
      message: "ID is required",
      function: "updateCollectionItemById",
    });

    return {
      success: false,
      message: "ID is required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Updating collection item by ID", {
      collection,
      id,
      data,
      message: "Data is required",
      function: "updateCollectionItemById",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Updating collection item by ID", {
      collection,
      id,
      data,
      function: "updateCollectionItemById",
    });

    const res = await rest<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}/${id}`,
      data,
      {
        method: "PATCH",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Updating collection item by ID failed",
        "updateCollectionItemById"
      );
    }

    return {
      success: true,
      data: res?.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Updating collection item by ID failed",
      "updateCollectionItemById"
    );
  }
};

export const bulkUpdateCollectionItemsByIds = async <T = any>({
  collection,
  ids,
  data,
}: CollectionDataWithIds): Promise<ApiResponse<T[]>> => {
  if (!collection) {
    logError("payloadApiCollections", "Bulk updating collection items by IDs", {
      collection,
      ids,
      data,
      message: "Collection is required",
      function: "bulkUpdateCollectionItemsByIds",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!ids) {
    logError("payloadApiCollections", "Bulk updating collection items by IDs", {
      collection,
      ids,
      data,
      message: "IDs are required",
      function: "bulkUpdateCollectionItemsByIds",
    });

    return {
      success: false,
      message: "IDs are required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Bulk updating collection items by IDs", {
      collection,
      ids,
      data,
      message: "Data is required",
      function: "bulkUpdateCollectionItemsByIds",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Bulk updating collection items by IDs", {
      collection,
      ids,
      data,
      function: "bulkUpdateCollectionItemsByIds",
    });

    const res = await rest<PayloadBulkResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}?where[id][$in]=${ids.join(",")}`,
      data,
      {
        method: "PATCH",
      }
    );

    if (res.errors && res.errors.length > 0) {
      return handleError(
        res,
        "Bulk updating collection items by IDs failed",
        "bulkUpdateCollectionItemsByIds"
      );
    }

    return {
      success: true,
      data: res.docs || [],
    };
  } catch (error) {
    return handleError(
      error,
      "Bulk updating collection items by IDs failed",
      "bulkUpdateCollectionItemsByIds"
    );
  }
};

/**
 * Deletes an item from the specified collection by ID
 * @param collection - The name of the collection
 * @param id - The ID of the item to delete
 * @returns A promise that resolves to the API response
 */
export const deleteCollectionItemById = async <T = any>({
  collection,
  id,
}: DeleteCollectionItem): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Deleting collection item by ID", {
      collection,
      id,
      message: "Collection is required",
      function: "deleteCollectionItemById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!id) {
    logError("payloadApiCollections", "Deleting collection item by ID", {
      collection,
      id,
      message: "ID is required",
      function: "deleteCollectionItemById",
    });

    return {
      success: false,
      message: "ID is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Deleting collection item by ID", {
      collection,
      id,
      function: "deleteCollectionItemById",
    });

    const res = await rest<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}/${id}`,
      null,
      {
        method: "DELETE",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Deleting collection item by ID failed",
        "deleteCollectionItemById"
      );
    }

    return {
      success: true,
      data: res.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Deleting collection item by ID failed",
      "deleteCollectionItemById"
    );
  }
};

/** Bulk delete items from the specified collection by array of IDs
 * @param collection - The name of the collection
 * @param ids - The array of IDs of the items to delete
 * @returns A promise that resolves to the API response
 * */

export const bulkDeleteCollectionItemsById = async <T = any>({
  collection,
  ids,
}: BulkDeleteCollectionItems): Promise<ApiResponse<T[]>> => {
  if (!collection) {
    logError("payloadApiCollections", "Bulk deleting collection items by ID", {
      collection,
      ids,
      message: "Collection is required",
      function: "bulkDeleteCollectionItemsById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!ids) {
    logError("payloadApiCollections", "Bulk deleting collection items by ID", {
      collection,
      ids,
      message: "IDs are required",
      function: "bulkDeleteCollectionItemsById",
    });

    return {
      success: false,
      message: "IDs are required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Bulk deleting collection items by ID", {
      collection,
      ids,
      function: "bulkDeleteCollectionItemsById",
    });

    const res = await rest<PayloadBulkResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}?where[id][$in]=${ids.join(",")}`,
      null,
      {
        method: "DELETE",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Bulk deleting collection items by ID failed",
        "bulkDeleteCollectionItemsById"
      );
    }

    return {
      success: true,
      data: res.docs || [],
    };
  } catch (error) {
    return handleError(
      error,
      "Bulk deleting collection items by ID failed",
      "bulkDeleteCollectionItemsById"
    );
  }
};

/**
 * Creates a new public item in the specified collection
 * @param collection - The name of the collection
 * @param data - The data for the new item
 * @returns A promise that resolves to the API response
 */
export const createPublicCollectionItem = async <T = any>({
  collection,
  data,
}: CollectionData): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Creating public collection item", {
      collection,
      data,
      message: "Collection is required",
      function: "createPublicCollectionItem",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!data) {
    logError("payloadApiCollections", "Creating public collection item", {
      collection,
      data,
      message: "Data is required",
      function: "createPublicCollectionItem",
    });

    return {
      success: false,
      message: "Data is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Creating public collection item", {
      collection,
      data,
      function: "createPublicCollectionItem",
    });

    const res = await restPublic<PayloadSingleResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}`,
      data,
      {
        method: "POST",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Creating public collection item failed",
        "createPublicCollectionItem"
      );
    }

    return {
      success: true,
      data: res?.doc,
    };
  } catch (error) {
    return handleError(
      error,
      "Creating public collection item failed",
      "createPublicCollectionItem"
    );
  }
};

/**
 * Retrieves an item from the specified collection by ID
 * PUBLIC API - No auth required
 * @param options - The options for retrieving the item
 * @returns A promise that resolves to the API response
 */
export const getPublicCollectionItemById = async <T = any>({
  collection,
  id,
  depth,
}: GetCollectionItem): Promise<ApiResponse<T>> => {
  if (!collection) {
    logError("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      message: "Collection is required",
      function: "getPublicCollectionItemById",
    });

    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!id) {
    logError("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      message: "ID is required",
      function: "getPublicCollectionItemById",
    });

    return {
      success: false,
      message: "ID is required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Getting collection item by ID", {
      collection,
      id,
      function: "getPublicCollectionItemById",
    });

    // const tag = createKey({
    //   userId: "public",
    //   collection,
    //   id,
    // });

    const res = await restPublic<PayloadGetResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}/${id}?depth=${depth || 1}`,
      null,
      {
        method: "GET",
        // TODO - handle caching properly
        // next: {
        //   tags: [tag],
        // },
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Getting collection item by ID failed",
        "getPublicCollectionItemById"
      );
    }

    return {
      success: true,
      data: res,
    };
  } catch (error) {
    return handleError(
      error,
      "Getting collection item by ID failed",
      "getPublicCollectionItemById"
    );
  }
};

/**
 * Retrieves a paginated collection
 * @param options - The options for retrieving the paginated collection
 * @returns A promise that resolves to the paginated collection
 */
export const getPagedCollection = async <T = any>({
  archived,
  collection,
  page,
  limit = PAGINATION_LIMIT,
  sort,
  where,
  join,
  depth = 0,
}: PaginationWithCollection): Promise<
  PagedCollectionResponse<T> | ErrorResponse
> => {
  if (!collection) {
    logError("payloadApiCollections", "Getting paged collection", {
      collection,
      page,
      limit,
      sort,
      where,
      join,
      depth,
      message: "Collection is required",
      function: "getPagedCollection",
    });

    return {
      success: false,
      message: "Collection is required",
    } as ErrorResponse;
  }

  let params = stringify(
    {
      archived,
      page,
      limit,
      where,
      depth,
    },
    { addQueryPrefix: true, skipNulls: true, encode: false }
  );

  // Add sort to the end, as comma separated values
  if (sort) {
    if (typeof sort === "string") {
      params = `${params}&sort=${sort}`;
    }

    if (Array.isArray(sort)) {
      params = `${params}&sort=${sort.join(",")}`;
    }
  }

  try {
    logInfo("payloadApiCollections", "Getting paged collection", {
      collection,
      page,
      limit,
      sort,
      where,
      depth,
      function: "getPagedCollection",
    });

    const joinParams = createJoinString(join || []);

    // Add join to the end, as comma separated values
    if (joinParams) {
      params = `${params}${joinParams ? `&${joinParams}` : ""}`;
    }

    const res = await rest<PayloadCollectionResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}${params}`,
      null,
      {
        method: "GET",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Getting paged collection failed",
        "getPagedCollection"
      );
    }

    return res;
  } catch (error) {
    return handleError(
      error,
      "Getting paged collection failed",
      "getPagedCollection"
    );
  }
};

/**
 * Retrieves the count of items in a collection
 * @param options - The options for retrieving the item count
 * @returns A promise that resolves to the item count
 */
export const getCollectionItemCount = async ({
  collection,
  where,
  join,
}: {
  collection: CollectionNames;
  where?: WhereClause;
  join?: Array<CollectionJoinParams>;
}): Promise<number> => {
  if (!collection) {
    logError("payloadApiCollections", "Getting collection item count", {
      collection,
      where,
      message: "Collection is required",
      function: "getCollectionItemCount",
    });

    return 0;
  }
  try {
    logInfo("payloadApiCollections", "Getting collection item count", {
      collection,
      where,
      function: "getCollectionItemCount",
    });

    const joinParams = createJoinString(join || []);

    const params = stringify(
      {
        where,
      },
      { addQueryPrefix: true, skipNulls: true, encode: false }
    );

    const res = await rest<PayloadCollectionResponse<any>>(
      `${PAYLOAD_URL}/api/${collection}${params}${joinParams ? `&${joinParams}` : ""}`,
      null,
      {
        method: "GET",
      }
    );
    if (res.errors) {
      handleError(
        res,
        "Getting collection item count failed",
        "getCollectionItemCount"
      );
      return 0;
    }
    return res?.totalDocs || 0;
  } catch (error) {
    handleError(
      error,
      "Getting collection item count failed",
      "getCollectionItemCount"
    );
    return 0;
  }
};

/**
 * Retrieves all items from a paginated collection.
 * @param options - The options for retrieving all items.
 * @returns A promise that resolves to all items.
 */
export const getAllCollectionItems = async <T = any>({
  collection,
  sort,
  where,
  depth,
}: GetAllCollectionItems): Promise<
  { totalItems: number; data: T[] } | ErrorResponse
> => {
  const limit = PAGINATION_LIMIT;

  const firstRes = await getPagedCollection<T>({
    collection,
    page: 1,
    limit,
    sort,
    where,
    depth,
  });

  // Check if the response is an error
  if ("success" in firstRes && firstRes.success === false) {
    return firstRes as ErrorResponse;
  }

  const pagedResponse = firstRes as PagedCollectionResponse<T>;
  const totalPages = pagedResponse.totalPages;
  const totalItems = pagedResponse.totalDocs;
  const data = pagedResponse.docs;

  // If there are no items, return an empty array.
  if (!totalPages) return { totalItems: 0, data: [] };

  // If there is only one page, return the data.
  if (totalPages === 1) {
    return {
      totalItems: totalItems || 0,
      data,
    };
  }

  // If there are multiple pages, get the rest of the data.
  const promises: Promise<PagedCollectionResponse<T> | ErrorResponse>[] = [];

  for (let i = 2; i < totalPages + 1; i++) {
    promises.push(
      getPagedCollection<T>({
        collection,
        sort,
        where,
        depth,
        limit,
        page: i,
      })
    );
  }

  const allData = await Promise.all(promises);

  // Check if any response is an error
  for (const response of allData) {
    if ("success" in response && response.success === false) {
      return response as ErrorResponse;
    }
  }

  const allDocs: T[] = (allData as PagedCollectionResponse<T>[]).reduce(
    (acc: T[], cur: PagedCollectionResponse<T>) => {
      return acc.concat(cur.docs);
    },
    data as T[]
  );

  return {
    totalItems: totalItems || 0,
    data: allDocs,
  };
};

/**
 * Bulk reorders items in a collection
 * @param collection - The name of the collection
 * @param itemIds - The array of IDs of the items to move
 * @returns A promise that resolves to the API response
 */
export const bulkReorderCollectionItems = async <T = any>({
  collection,
  itemIds,
}: {
  collection: CollectionNames;
  itemIds: string[];
}): Promise<ApiResponse<T[]>> => {
  if (!collection) {
    logError("payloadApiCollections", "Bulk reordering collection items", {
      collection,
      itemIds,
      message: "Collection is required",
      function: "bulkReorderCollectionItems",
    });
    return {
      success: false,
      message: "Collection is required",
    };
  }

  if (!itemIds || itemIds.length === 0) {
    logError("payloadApiCollections", "Bulk reordering collection items", {
      collection,
      itemIds,
      message: "Docs to move are required",
      function: "bulkReorderCollectionItems",
    });
    return {
      success: false,
      message: "Docs to move are required",
    };
  }

  try {
    logInfo("payloadApiCollections", "Bulk reordering collection items", {
      collection,
      itemIds,
      function: "bulkReorderCollectionItems",
    });

    const targetId = itemIds[0];
    // Split out the target as docsToMove cannot contain the targetId.
    const allOtherDocs = itemIds.filter((doc: string) => doc !== targetId);

    const res = await rest<PayloadBulkResponse<T>>(
      `${PAYLOAD_URL}/api/reorder/bulk`,
      {
        collectionSlug: collection,
        docsToMove: allOtherDocs,
        newKeyWillBe: "greater",
        orderableFieldName: "_order", // currently all collections have the same field name
        target: {
          id: targetId,
          key: "a1",
        },
      },
      {
        method: "POST",
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Bulk reordering collection items failed",
        "bulkReorderCollectionItems"
      );
    }

    return {
      success: true,
      data: res.docs || [],
    };
  } catch (error) {
    return handleError(
      error,
      "Bulk reordering collection items failed",
      "bulkReorderCollectionItems"
    );
  }
};

/**
 * Retrieves a public paginated collection
 * @param options - The options for retrieving the paginated collection
 * @returns A promise that resolves to the paginated collection
 */
export const getPublicPagedCollection = async <T = any>({
  archived,
  collection,
  page,
  limit = PAGINATION_LIMIT,
  sort,
  where,
  join,
  depth = 0,
}: PaginationWithCollection): Promise<
  PagedCollectionResponse<T> | ErrorResponse
> => {
  if (!collection) {
    logError("payloadApiCollections", "Getting public paged collection", {
      collection,
      page,
      limit,
      sort,
      where,
      depth,
      message: "Collection is required",
      function: "getPublicPagedCollection",
    });

    return {
      success: false,
      message: "Collection is required",
    } as ErrorResponse;
  }

  let params = stringify(
    {
      archived,
      page,
      limit,
      sort,
      where,
      depth,
    },
    { addQueryPrefix: true, skipNulls: true, encode: false }
  );

  const joinParams = createJoinString(join || []);

  // Add join to the end, as comma separated values
  if (joinParams) {
    params = `${params}${joinParams ? `&${joinParams}` : ""}`;
  }

  try {
    logInfo("payloadApiCollections", "Getting public paged collection", {
      collection,
      page,
      limit,
      sort,
      where,
      depth,
      function: "getPublicPagedCollection",
    });

    // const tag = createKey({
    //   userId: "public",
    //   collection,
    //   all: true,
    // });

    const res = await restPublic<PayloadCollectionResponse<T>>(
      `${PAYLOAD_URL}/api/${collection}${params}`,
      null,
      {
        method: "GET",
        // TODO - handle caching properly
        // next: {
        //   tags: [tag],
        // },
      }
    );

    if (res.errors) {
      return handleError(
        res,
        "Getting paged public collection failed",
        "getPublicPagedCollection"
      );
    }

    return res;
  } catch (error) {
    return handleError(
      error,
      "Getting paged public collection failed",
      "getPublicPagedCollection"
    );
  }
};

/**
 * Retrieves all items from a public paginated collection.
 * @param options - The options for retrieving all items.
 * @returns A promise that resolves to all items.
 */
export const getAllPublicCollectionItems = async <T = any>({
  collection,
  sort,
  where,
  depth,
}: GetAllCollectionItems): Promise<
  { totalItems: number; data: T[] } | ErrorResponse
> => {
  const limit = PAGINATION_LIMIT;

  const firstRes = await getPublicPagedCollection<T>({
    collection,
    page: 1,
    limit,
    sort,
    where,
    depth,
  });

  // Check if the response is an error
  if ("success" in firstRes && firstRes.success === false) {
    return firstRes as ErrorResponse;
  }

  const pagedResponse = firstRes as PagedCollectionResponse<T>;
  const totalPages = pagedResponse.totalPages;
  const totalItems = pagedResponse.totalDocs;
  const data = pagedResponse.docs;

  // If there are no items, return an empty array.
  if (!totalPages) return { totalItems: 0, data: [] };

  // If there is only one page, return the data.
  if (totalPages === 1) {
    return {
      totalItems: totalItems || 0,
      data,
    };
  }

  // If there are multiple pages, get the rest of the data.
  const promises: Promise<PagedCollectionResponse<T> | ErrorResponse>[] = [];

  for (let i = 2; i < totalPages + 1; i++) {
    promises.push(
      getPublicPagedCollection<T>({
        collection,
        sort,
        where,
        depth,
        limit,
        page: i,
      })
    );
  }

  const allData = await Promise.all(promises);

  // Check if any response is an error
  for (const response of allData) {
    if ("success" in response && response.success === false) {
      return response as ErrorResponse;
    }
  }

  const allDocs: T[] = (allData as PagedCollectionResponse<T>[]).reduce(
    (acc: T[], cur: PagedCollectionResponse<T>) => {
      return acc.concat(cur.docs);
    },
    data as T[]
  );

  return {
    totalItems: totalItems || 0,
    data: allDocs,
  };
};
